import { action, computed, observable, runInAction } from 'mobx'
import { task } from 'mobx-task'
import type { ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import type {
  SnackbarErrorHandlerFactory,
  SnackbarFactory,
} from '@/features/shell'
import type { TxfWorklayerJobStore } from '@/features/jobs/details/tax-type/tax-prep-organizer/TxfWorklayerJobStore'
import {
  type TaxPreparationData,
  type TxfWorklayerJob,
  type FilingYear,
} from '@/features/jobs/details/tax-type/tax-prep-organizer/models'
import { TaxPrepOrganizerViewModel } from './TaxPrepOrganizerViewModel'
import { SwitchYearDialogViewModel } from './components/SwitchYearDialog/SwitchYearDialogViewModel'
import { ConnectWithProDialogViewModel } from './components/ConnectWithProDialog/ConnectWithProDialogViewModel'
import type TxfJob from '../txf/TxfJob'
import type { InfoGatheringStore } from '../info-gathering/InfoGatheringStore'
import type { Checklist } from '../info-gathering/Checklist'
import type { GetExternalUrlDelegate } from '@/features/shell/platform/types'
import { links } from '@/utils/link'
import type { JobGist } from '@/features/jobs/JobGist'

export class TaxPrepOrganizerWrapperViewModel implements ActivationLifecycle {
  readonly lifecycle = new LifecycleTracker()

  @observable accessor jobGist: JobGist
  @observable accessor txfJob: TxfJob
  @observable accessor txfWorklayerJob: TxfWorklayerJob
  @observable accessor checklist: Checklist
  @observable accessor selectedYear: FilingYear | null = null
  @observable accessor taxPrepData: TaxPreparationData | null = null
  @observable accessor innerVm: TaxPrepOrganizerViewModel | null = null

  readonly switchYearDialogVm: SwitchYearDialogViewModel
  readonly connectWithProDialogVm: ConnectWithProDialogViewModel

  constructor(
    private readonly _getExternalUrlDelegate: GetExternalUrlDelegate,
    private readonly _createSnackbarErrorHandler: SnackbarErrorHandlerFactory,
    private readonly _newSnackbar: SnackbarFactory,
    private readonly _txfWorklayerJobStore: TxfWorklayerJobStore,
    private readonly _infoGatheringStore: InfoGatheringStore,
    jobGist: JobGist,
    txfJob: TxfJob,
    txfWorklayerJob: TxfWorklayerJob,
    checklist: Checklist,
  ) {
    this.jobGist = jobGist
    this.txfJob = txfJob
    this.txfWorklayerJob = txfWorklayerJob
    this.checklist = checklist
    this.switchYearDialogVm = new SwitchYearDialogViewModel({
      onSave: (navigateTo) =>
        this.innerVm?.save(navigateTo) ?? Promise.resolve(),
      onNavigate: (year) => void this._proceedToYear(year),
    })
    this.connectWithProDialogVm = new ConnectWithProDialogViewModel({
      onConfirm: () => this._completeInfoGathering(),
    })
  }

  @computed
  get jobId() {
    return this.txfWorklayerJob.id
  }

  @computed
  get isLoading(): boolean {
    return this.fetchTaxPrepData.pending
  }

  @computed
  get showConnectWithAPro(): boolean {
    if (this.jobGist.status !== 'InfoGathering') return false
    return (
      this.checklist.completion.type === 'Completed' ||
      (this.checklist.completion.type === 'Incomplete' &&
        this.checklist.completion.allRequiredItemsCompleted)
    )
  }

  fetchTaxPrepData = task(async (signal?: AbortSignal) => {
    if (this.selectedYear === null) return

    const data = await this._txfWorklayerJobStore.fetchTxfTaxPreparationData(
      this.txfJob.jobId,
      this.selectedYear.year,
      signal,
    )
    if (!data) {
      throw new Error('Failed to fetch tax preparation data')
    }

    runInAction(() => {
      this.taxPrepData = data
      this._createInnerVm()
    })
  })

  @action.bound
  private _createInnerVm() {
    if (this.innerVm || !this.taxPrepData) return

    this.innerVm = new TaxPrepOrganizerViewModel(
      this._createSnackbarErrorHandler,
      this._newSnackbar,
      this._txfWorklayerJobStore,
      () => this.txfWorklayerJob,
      () => this.selectedYear!,
      () => this.taxPrepData!,
      this.fetchTaxPrepData,
      this._proceedToYear,
    )
    this.lifecycle.manage(this.innerVm)
    this.innerVm.activate()
  }

  @action.bound
  selectYear(filingYear: FilingYear) {
    if (this.selectedYear === null || this.selectedYear.id === filingYear.id)
      return

    this.switchYearDialogVm.showDialog(filingYear)
  }

  private _proceedToYear = task(async (filingYear: FilingYear) => {
    runInAction(() => {
      this.selectedYear = filingYear
    })
    await this.fetchTaxPrepData()
  })

  @action.bound
  showConnectWithProDialog() {
    this.connectWithProDialogVm.showDialog()
  }

  private async _completeInfoGathering() {
    await this._infoGatheringStore
      .completeInfoGathering(this.jobId)
      .catch(
        this._createSnackbarErrorHandler('Failed to complete info gathering'),
      )

    const handle = this._getExternalUrlDelegate().createHandle({
      sameWindow: true,
    })
    handle.open(links.jobDetails(this.jobId))
  }

  activate() {
    this.lifecycle.activate()

    if (this.txfWorklayerJob.activeFilingYears.length > 0) {
      void this._proceedToYear(this.txfWorklayerJob.activeFilingYears[0])
    }
  }

  deactivate() {
    this.lifecycle.deactivate()
  }
}
